import {
    faClock,
    faCircleCheck,
    faChevronUp,
    faChevronDown,
    faExclamationTriangle,
    faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { DEFAULT_POLICY } from './policy-content';

/**
 * Component: Policy (Điều Khoản)
 * Mô tả:
 *   Hiển thị thông tin chi tiết về quy trình hoàn tiền, điều kiện chấp nhận hoàn tiền
 *   và các trường hợp bị từ chối hoàn tiền. Cho phép người dùng mở rộng để xem thêm.
 *   Nội dung lấy từ `policy-content.js` (DEFAULT_POLICY hoặc bản riêng của từng sàn).
 *
 * Props:
 * - fromColorBg (string): Màu gradient bắt đầu cho nền biểu tượng (mặc định: "từ cam").
 * - toColorBg (string): Màu gradient kết thúc cho nền biểu tượng (mặc định: "đến cam").
 * - data (object): Nội dung điều khoản (mặc định DEFAULT_POLICY), xem policy-content.js.
 */
function Policy({ fromColorBg, toColorBg, data = DEFAULT_POLICY }) {
    const [isOpenMore, setIsOpenMore] = useState(false);

    // Một dòng điều khoản: icon bên trái + nội dung bên phải
    const renderRow = (row, index, isLast) => (
        <div
            key={index}
            className={`flex items-center py-2 ${isLast ? '' : 'border-b'}`}
        >
            <div className="h-10 aspect-square flex justify-center items-center text-center text-xl">
                <FontAwesomeIcon icon={row.icon} />
            </div>
            <p className="text-xs">
                {row.text}
                {row.link && (
                    <>
                        {' '}
                        <Link to={row.link.to} className="text-blue-500 hover:text-blue-700">
                            {row.link.label}
                        </Link>
                    </>
                )}
            </p>
        </div>
    );

    return (
        <div onClick={(e) => e.stopPropagation()} className="bg-surface p-2">
            {/* Tiêu đề với biểu tượng và dòng giới thiệu */}
            <div className="flex gap-1 justify-start items-center">
                <div
                    className={`h-10 p-2 aspect-square bg-gradient-to-br ${fromColorBg || 'from-orange-500'} ${toColorBg || 'to-orange-600'} rounded-full`}
                >
                    <img
                        src="img/ui/activity_white.svg"
                        alt="policy"
                        className="h-full w-full object-cover"
                    />
                </div>
                <div className="flex flex-col text-sm text-content-2">
                    <span>Tham khảo</span>
                    <span className="font-bold">ĐIỀU KHOẢN HOÀN TIỀN</span>
                </div>
            </div>

            {/* Nội dung về chính sách hoàn tiền */}
            <div className="bg-surface mt-2">
                <div className="p-2">
                    <h3 className="text-xs font-bold text-orange-600 py-1">
                        Quy trình và thời gian xử lý hoàn tiền
                    </h3>
                    <div className="rounded-xl border">
                        <div className="flex items-start items-center py-2">
                            <div className="h-10 aspect-square flex justify-center items-center text-center text-xl">
                                <FontAwesomeIcon icon={faClock} />
                            </div>
                            <div className="flex-1">
                                {data.platformTimeline.map((item, index) => (
                                    <div key={index} className="py-2">
                                        <p className="text-xs">
                                            <span className="font-bold">{item.platform}:</span> {item.description}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="border-t">
                            {data.timelineRows.map((row, index) =>
                                renderRow(row, index, index === data.timelineRows.length - 1)
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-2">
                    <h3 className="text-xs font-bold text-orange-600 py-1">
                        Điều kiện chấp nhận hoàn tiền
                    </h3>
                    <div className="rounded-xl border">
                        {data.accept.map((row, index) =>
                            renderRow(row, index, index === data.accept.length - 1)
                        )}
                    </div>
                </div>

                <div className="p-2">
                    <h3 className="text-xs font-bold text-orange-600 py-1">
                        Từ chối hoàn tiền nếu
                    </h3>
                    <div className="rounded-xl border">
                        {data.reject.map((row, index) =>
                            renderRow(row, index, index === data.reject.length - 1)
                        )}
                    </div>
                </div>

                {/* Nội dung mở rộng */}
                <div className="flex justify-center items-center">
                    <button
                        onClick={() => setIsOpenMore(prev => !prev)}
                        className="bg-surface-raised py-1 px-2 w-40 flex justify-center items-center gap-2 rounded"
                    >
                        <span>{isOpenMore ? "THU GỌN" : "XEM THÊM"}</span>
                        <FontAwesomeIcon
                            icon={isOpenMore ? faChevronUp : faChevronDown}
                            className="text-sm"
                        />
                    </button>
                </div>
                {isOpenMore && (
                    <div id="additionalInfo" className="mt-2">
                        <div className="p-2">
                            <h4 className="text-xs font-bold text-red-600 py-1">
                                Điều kiện chấp nhận hoàn tiền
                            </h4>
                            <div className="rounded-xl border">
                                <div className="p-2">
                                    {data.expandedAccept.map((text, index) => (
                                        <div
                                            key={index}
                                            className={`flex gap-2 items-center py-2 ${index === data.expandedAccept.length - 1 ? '' : 'mb-1 border-b'}`}
                                        >
                                            <FontAwesomeIcon icon={faCircleCheck} className="text-green-600 text-sm mt-1" />
                                            <p className="text-xs">{text}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="p-2">
                            <h4 className="text-xs font-bold text-red-600 py-1">
                                Từ chối hoàn tiền nếu
                            </h4>
                            <div className="rounded-xl border">
                                <div className="p-2">
                                    {data.expandedReject.map((row, index) => (
                                        <div
                                            key={index}
                                            className={`flex gap-2 items-center py-2 ${index === data.expandedReject.length - 1 ? '' : 'mb-1 border-b'}`}
                                        >
                                            <FontAwesomeIcon icon={row.icon} className={`${row.iconClass} text-sm mt-1`} />
                                            <p className="text-xs">
                                                {row.text}
                                                {row.href && (
                                                    <>
                                                        {' '}
                                                        <a href={row.href} className="underline font-bold" target="_blank" rel="noreferrer">
                                                            {row.linkLabel}
                                                        </a>
                                                        .
                                                    </>
                                                )}
                                            </p>
                                        </div>
                                    ))}
                                    <div className="flex gap-2 mb-1 items-center border-t pt-2">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 text-sm" />
                                        <p className="text-xs">
                                            Các đơn hàng có dấu hiệu gian lận bao gồm:
                                        </p>
                                    </div>
                                    <ul className="text-xs list-disc pl-8">
                                        {data.fraudList.map((item, index) => (
                                            <li key={index}>{item}</li>
                                        ))}
                                    </ul>
                                    <div className="flex gap-2 items-center border-t mt-2 py-2">
                                        <FontAwesomeIcon icon={faCircleInfo} className="text-blue-600 text-sm mt-1" />
                                        <p className="text-xs">{data.closingNote}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Policy;
